import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import type { MediaEntityInfo } from "@/types/api";
import type { MediaEntityKind } from "@/api/api";

/** Mirrors the backend's MEDIA_BATCH_MAX_ITEMS (rest-api/lambdas/entity/main.py). */
export const MEDIA_BATCH_MAX_ITEMS = 100;

export interface MediaBatchItemRef {
  readonly kind: MediaEntityKind;
  readonly id: string;
}

interface MediaBatchResponseItem {
  readonly id: string;
  readonly kind: string;
  readonly url: string;
  readonly info?: MediaEntityInfo;
  readonly thumbnail_url?: string;
}

export interface MediaEntityData {
  readonly url: string;
  readonly displayName: string;
  readonly info?: MediaEntityInfo;
  readonly thumbnailUrl?: string;
}

export function mediaBatchRefKey(ref: MediaBatchItemRef): string {
  return `${ref.kind}:${ref.id}`;
}

/** Splits a list of refs into request-sized pages for POST /{project}/media/batch. */
export function chunkMediaBatchRefs(
  refs: readonly MediaBatchItemRef[],
  chunkSize: number = MEDIA_BATCH_MAX_ITEMS
): MediaBatchItemRef[][] {
  const chunks: MediaBatchItemRef[][] = [];
  for (let i = 0; i < refs.length; i += chunkSize) {
    chunks.push(refs.slice(i, i + chunkSize));
  }

  return chunks;
}

function toMediaEntityData(item: MediaBatchResponseItem): MediaEntityData {
  const thumbnailUrl = typeof item.thumbnail_url === "string" ? item.thumbnail_url.trim() : "";
  return {
    url: item.url,
    displayName: item.info?.display_name ?? item.id,
    ...(item.info === undefined ? {} : { info: item.info }),
    ...(thumbnailUrl === "" ? {} : { thumbnailUrl })
  };
}

/**
 * Fetches presigned URL + info for many media refs via POST /{project}/media/batch, chunking as
 * needed. Returns a map keyed by `mediaBatchRefKey` so callers can look up results per ref; refs
 * the backend couldn't resolve (bad kind/id, not found) are simply absent from the map.
 */
export async function fetchMediaBatch(
  axiosClient: AxiosInstance,
  projectId: string,
  refs: readonly MediaBatchItemRef[]
): Promise<Map<string, MediaEntityData>> {
  const result = new Map<string, MediaEntityData>();
  const chunks = chunkMediaBatchRefs(refs);

  const responses = await Promise.all(
    chunks.map(async (chunk) => {
      const { data } = await axiosClient.post<{ items: MediaBatchResponseItem[] }>(
        `/${encodeURIComponent(projectId)}/media/batch`,
        { items: chunk.map((ref) => ({ kind: ref.kind, id: ref.id })) }
      );
      return data.items ?? [];
    })
  );

  for (const items of responses) {
    for (const item of items) {
      result.set(mediaBatchRefKey({ kind: item.kind as MediaEntityKind, id: item.id }), toMediaEntityData(item));
    }
  }

  return result;
}

/** Matches useMediaEntity/useImageEntity/useVideoEntity's own staleTime (media.ts) - a ref this
 * fresh in the cache is a `useMediaEntity` hook that already mounted and fetched it, not one
 * about to fire its own request, so re-fetching it here would just be redundant network traffic. */
const MEDIA_REFS_BATCH_PREFETCH_STALE_MS = 60_000;

function isFreshInCache(queryClient: QueryClient, kind: MediaEntityKind, projectId: string, id: string): boolean {
  const state = queryClient.getQueryState([kind, projectId, id]);
  return state?.dataUpdatedAt !== undefined && Date.now() - state.dataUpdatedAt < MEDIA_REFS_BATCH_PREFETCH_STALE_MS;
}

/**
 * Warms the `[kind, projectId, id]` cache (the same query key `useMediaEntity` reads) for a
 * batch of media refs via one POST /{project}/media/batch call, skipping any ref already fresh
 * in the cache. Call this before/alongside rendering many `useImageEntity`/`useVideoEntity`
 * consumers at once (Library grid, Timeline strip) so they find their data already cached
 * instead of each firing its own GET /{project}/{kind}/{id} - individually cheap, but N of them
 * firing together turns one page load into N serialized round trips to real AWS.
 */
export async function prefetchMediaRefsBatch(
  queryClient: QueryClient,
  axiosClient: AxiosInstance,
  projectId: string,
  refs: readonly MediaBatchItemRef[]
): Promise<void> {
  const seen = new Set<string>();
  const toFetch: MediaBatchItemRef[] = [];

  for (const ref of refs) {
    const key = mediaBatchRefKey(ref);
    if (seen.has(key) || isFreshInCache(queryClient, ref.kind, projectId, ref.id)) {
      continue;
    }

    seen.add(key);
    toFetch.push(ref);
  }

  if (toFetch.length === 0) {
    return;
  }

  const byRef = await fetchMediaBatch(axiosClient, projectId, toFetch);
  for (const [key, data] of byRef) {
    const [kind, id] = key.split(":") as [MediaEntityKind, string];
    queryClient.setQueryData([kind, projectId, id], data);
  }
}
